import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useT, stripHi, isPublicPath, hiPath } from "@/lib/i18n";

export const SITE = "https://www.vectra-ai.in";
export const SITE_NAME = "Vectra by Swastik AI Labs";
const DEFAULT_IMAGE = `${SITE}/og-vectra.png`;
const SOCIALS = ["https://www.instagram.com/akhilesh.mishra/", "https://x.com/Akhileshmishras", "https://www.threads.com/@akhilesh.mishra", "https://t.me/+WO0s40101fFiMDll", "https://www.linkedin.com/in/akhilesh-mishra-61268a54/"];

function upsert(selector, create) {
  let el = document.head.querySelector(selector);
  if (!el) { el = create(); document.head.appendChild(el); }
  return el;
}
function meta(attr, key, content) {
  if (content == null) return;
  const el = upsert(`meta[${attr}="${key}"]`, () => { const m = document.createElement("meta"); m.setAttribute(attr, key); return m; });
  el.setAttribute("content", content);
}

export function Seo({ title, description, path, image, noindex = false, type = "website", jsonLd = [] }) {
  const { pathname } = useLocation();
  const { lang } = useT();
  const base = stripHi(path ?? pathname);
  const isPublic = isPublicPath(base) && !noindex;
  const url = lang === "hi" && isPublic ? `${SITE}${hiPath(base)}` : `${SITE}${base}`;
  useEffect(() => {
    const fullTitle = title ? (title.length > 55 ? title : `${title} | Vectra`) : SITE_NAME;
    document.title = fullTitle;
    document.documentElement.lang = lang === "hi" ? "hi-IN" : "en-IN";
    meta("name", "description", description);
    meta("name", "robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
    if (process.env.REACT_APP_GSC_VERIFICATION) meta("name", "google-site-verification", process.env.REACT_APP_GSC_VERIFICATION);
    upsert('link[rel="canonical"]', () => { const l = document.createElement("link"); l.rel = "canonical"; return l; }).setAttribute("href", url);
    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((l) => l.remove());
    if (isPublic) {
      [["en-IN", `${SITE}${base}`], ["hi-IN", `${SITE}${hiPath(base)}`], ["x-default", `${SITE}${base}`]].forEach(([hl, href]) => {
        const l = document.createElement("link"); l.rel = "alternate"; l.hreflang = hl; l.href = href; document.head.appendChild(l);
      });
    }
    meta("property", "og:type", type);
    meta("property", "og:site_name", SITE_NAME);
    meta("property", "og:locale", lang === "hi" ? "hi_IN" : "en_IN");
    meta("property", "og:locale:alternate", lang === "hi" ? "en_IN" : "hi_IN");
    meta("property", "og:title", fullTitle);
    meta("property", "og:description", description);
    meta("property", "og:url", url);
    meta("property", "og:image", image || DEFAULT_IMAGE);
    if (!image) { meta("property", "og:image:width", "1200"); meta("property", "og:image:height", "630"); }
    meta("property", "og:image:alt", fullTitle);
    meta("name", "twitter:card", "summary_large_image");
    meta("name", "twitter:title", fullTitle);
    meta("name", "twitter:description", description);
    meta("name", "twitter:image", image || DEFAULT_IMAGE);
    meta("name", "twitter:image:alt", fullTitle);
    document.head.querySelectorAll('script[data-seo-jsonld]').forEach((s) => s.remove());
    jsonLd.filter(Boolean).forEach((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json"; s.setAttribute("data-seo-jsonld", "1");
      s.text = JSON.stringify({ "@context": "https://schema.org", ...obj });
      document.head.appendChild(s);
    });
  }, [title, description, url, image, noindex, type, lang, isPublic, base, JSON.stringify(jsonLd)]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export const ORG_LD = {
  "@type": "Organization", "@id": `${SITE}/#org`, name: "Swastik AI Labs Pvt Ltd", alternateName: "Vectra", brand: { "@type": "Brand", name: "Vectra" }, url: SITE, logo: { "@type": "ImageObject", url: `${SITE}/vectra-logo.png`, width: 512, height: 512 },
  founder: { "@type": "Person", name: "Akhilesh Mishra", url: "https://www.linkedin.com/in/akhilesh-mishra-61268a54/" }, sameAs: SOCIALS, contactPoint: [{ "@type": "ContactPoint", contactType: "customer support", url: `${SITE}/contact`, availableLanguage: ["en", "hi"] }],
};

export const breadcrumbLd = (items) => ({
  "@type": "BreadcrumbList",
  itemListElement: items.map(([name, path], i) => ({ "@type": "ListItem", position: i + 1, name, item: `${SITE}${path}` })),
});

export const faqLd = (qa) => ({ "@type": "FAQPage", mainEntity: qa.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) });

export const appLd = (packs = []) => ({
  "@type": "SoftwareApplication", name: "Vectra", applicationCategory: "MultimediaApplication", operatingSystem: "Web", url: SITE, image: DEFAULT_IMAGE,
  description: "AI content production studio: scripted video, AI visuals, HD voice-over, music with auto-ducking, Whisper captions, vertical reframing, talking presenters and publishing — every output is a real downloadable file.",
  publisher: { "@type": "Organization", name: "Swastik AI Labs", url: SITE },
  offers: packs.length ? packs.map((p) => ({ "@type": "Offer", name: `${p.name} pack`, price: String(p.price), priceCurrency: "INR", url: `${SITE}/pricing`, availability: "https://schema.org/InStock", category: "Prepaid 30-day pack" })) : undefined,
});
